import { Plugins, resolve } from '@leverege/plugin';
import Unit from '@leverege/unit';
import NoSettersError from "./NoSettersError.js"; // Note: This currently has the attribute contributing the display names
// and groups for the attribute. It is possible that different attribute
// objects representing the same attribute for different types might return
// different names. This is a problem we can perhaps solve using i18n lib, and
// remove the burden of display names fro the attribute all together
/* 
Attributes:
  name : <string> identifies the type. should be 'unique' in that same name === same type
  displayName : <string>, human readable name
  objectType : <string> the type of object which this can be used to invoke data
  valueType : <string> the type of value returned. string, float, geoPoint, length, duration, etc
  layout : {
    sort : <string>,
    group : [ <string> ],
    groupNames : [ <string> ],
  }
  get : function( item, cxt ) returns the items's value of the attribute 
  set : function( objRef, value, cxt ) // may need multiple setters some that return null, some that return a promise
  canSet : function( item, value, cxt ) // true false

Attributes (lookup)
  What attributes supports type X // for editor
  get( name, type, item, cxt ) // returns the value

  */
function nameTypeKey(name, objectType) {
  return `${name}:${objectType}`;
}
function idTypeKey(id, objectType) {
  if (id == null) {
    return null;
  }
  return `${id}:${objectType}`;
}

/**
 * Rebuilds the lookup for the associations
 */
function processor(attrs) {
  const types = new Map(); // name:type -> attribute lookup
  const named = new Map(); // name -> attribute lookup
  const lookup = new Map(); // type -> [ attributes ]
  const valueTypes = new Map(); // namd -> valueType lookup
  const aliased = new Map();
  for (let n = 0; n < attrs.length; n++) {
    const attr = attrs[n];
    if (typeof attr.objectType !== 'string' || typeof attr.name !== 'string' || typeof attr.valueType !== 'string' || typeof attr.get !== 'function' || attr.set != null && typeof attr.set !== 'function' || attr.set && typeof attr.canSet !== 'function') {
      // // eslint-disable-next-line no-console
      // console.log( 'Ignoring Illegal Attribute', attr )
      continue;
    }

    // Add to object types lookup
    const vArr = types.get(attr.objectType);
    if (vArr == null) {
      types.set(attr.objectType, [attr]);
    } else {
      vArr.push(attr);
    }

    // Add to object name lookup
    const nArr = named.get(attr.name);
    if (nArr == null) {
      named.set(attr.name, [attr]);
    } else {
      nArr.push(attr);
    }

    // add to by-type lookup
    const key = nameTypeKey(attr.name, attr.objectType);
    const tArr = lookup.get(key);
    if (tArr == null) {
      lookup.set(key, [attr]);
    } else {
      // Going to allow this for now. I believe we may need Multiple
      // setters - though perhaps not.
      // eslint-disable-next-line no-console
      // console.log( `Multiple Attributes registered for ${key}` )
      tArr.push(attr);
    }
    const vType = valueTypes.get(attr.name);
    if (vType == null) {
      valueTypes.set(attr.name, attr.valueType);
    } else if (vType !== attr.valueType) {
      console.warn(`Attribute with name "${attr.name}" attempting to set attribute valueType to ${attr.valueType}.
      Already set to ${vType} and will be ignored.`);
    }
    if (attr.isAlias) {
      const key = idTypeKey(attr.attrId, attr.objectType);
      if (key) {
        const aType = aliased.get(key);
        if (!aType) {
          aliased.set(key, [attr]);
        } else {
          aType.push(attr);
        }
      }
    }
  }
  return {
    lookup,
    types,
    named,
    valueTypes,
    aliased
  };
}
const view = Plugins.createView('Attribute', {
  processor
});

/**
 * Returns an Array of attributes that can be used
 * for the given type of object
 */
function getAttributesFor(type, options) {
  const {
    types,
    aliased
  } = view.get();
  const {
    removeAliasedDuplicates = false
  } = options || {};
  if (removeAliasedDuplicates) {
    const t = types.get() || [];
    if (t.length === 0) {
      return t;
    }
    return t.filter(attr => {
      if (!attr.isAlias) {
        return aliased.get(idTypeKey(attr.attrId, attr.objectType)) != null;
      }
      return true;
    });
  }
  return types.get(type) || [];
}

/**
 * Returns the named attributes
 */
function getAttributes(name) {
  const {
    named
  } = view.get();
  const attrs = named.get(name);
  if (attrs) {
    return attrs;
  }
  return [];
}

/**
 * Returns the display name for the attribute
 */
function getDisplayName(name, objectType) {
  const attrs = view.get().named.get(name);
  if (objectType) {
    const attr = getAttribute(name, objectType);
    if (attr) {
      return resolve(attr.displayName || attr.name);
    }
  }

  // TODO: Without object type, get the shared name. for now, the first
  if (attrs && attrs[0] != null) {
    return resolve(attrs[0].displayName || attrs[0].name);
  }
  return null;
}

/**
 * Returns the named attribute for the type
 */
function getAttribute(name, objectType) {
  const {
    lookup
  } = view.get();
  const key = nameTypeKey(name, objectType);
  const attrs = lookup.get(key);
  if (attrs) {
    return attrs[0];
  }
  return null;
}

/**
 * Looks up and returns the attribute value for
 * the given name, type of object, object, and context. This
 * can be invoked in two ways. either as a separate group of 
 * objects like get( 'name', objRef.type, objRef.data ), or by
 * using the objRef itself: get( 'name', objRef )
 */
function get(name, objectType, object, cxt) {
  if (objectType == null) {
    return undefined;
  }
  const isObjRef = typeof objectType === 'object';
  let objRef = null;
  // Are we passing in an objRef?
  if (isObjRef) {
    cxt = object; // eslint-disable-line no-param-reassign
    objRef = objectType;
    object = objectType.data; // eslint-disable-line no-param-reassign
    objectType = objectType.type; // eslint-disable-line no-param-reassign
  }
  const attr = getAttribute(name, objectType);
  if (attr) {
    try {
      if (attr.usesObjRef) {
        return attr.get(objRef || {
          type: objectType,
          data: object
        }, cxt);
      }
      return attr.get(object, cxt);
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error(`Attribute get for ${objectType}:${name} returned error`, ex);
    }
  }
  return undefined;
}
function getOption(name, objectType, optionName) {
  if (objectType == null) {
    return undefined;
  }
  const attr = getAttribute(name, objectType);
  if (attr) {
    try {
      return attr.getOption?.(optionName);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Attribute getOption for ${objectType}:${name} returned error`, err);
    }
  }
  return undefined;
}
function getValueType(name) {
  const {
    valueTypes
  } = view.get();
  const valueType = valueTypes.get(name);
  return valueType;
}

/**
 * Looks up and calls the attribute canSet function with
 * the given name, objectRef, and context. This 
 * should return true if a set() might succeed, false otherwise.
 * If no current value is available, use undefined.
 */
function canSet(name, objRef, value, cxt) {
  const attr = getAttribute(name, objRef?.type);
  if (attr?.canSet) {
    return attr.canSet(objRef, value, cxt);
  }
  return false;
}

/**
 * Looks up and calls the attribute setter function with
 * the given name, objectRef, and context. 
 * @param {string} name the name of the attribute to set
 * @param {object} objRef an object with at minimum id, ref, type, (optional data)
 * @param {any} value the value to set
 * @param {Transaction|true} transaction If non-null, the setter should contribute 
 * its values setting attributes to a custom object and return the object. This object
 * should have a <Promise> run() function. If necessary, GlobalState.dispatch can be
 * used by the setters. This run() function should be invoked at completion of calling
 * set on all the attributes 
 * @return If a transation is given (or true) this will return a transactional object, 
 * If there are no setters available for the name, this will return transaction if it is
 * non null/undefined. If a transaction is not given, this will attempt to set the
 * value on the object now, returning a Promise to indicate its success or failure. If 
 * a transaction is not specified, and a setter does not exist, a rejected promise with
 * a NoSetterError will be returned
 */
function set(name, objRef, value, cxt, transaction) {
  const attr = getAttribute(name, objRef?.type);
  if (attr?.set) {
    let setVal = value;
    const unit = value?.unit;
    const baseUnit = attr?.baseUnit;
    if (unit) {
      setVal = value.value;
      if (baseUnit && unit !== baseUnit) {
        setVal = Unit.convert(value.value, unit, baseUnit);
      }
    }
    return attr.set(objRef, setVal, cxt, transaction);
  }
  if (transaction) {
    return transaction;
  }
  return Promise.reject(new NoSettersError('No setters', name, objRef?.type));
}
export default {
  getAttributesFor,
  getAttributes,
  getDisplayName,
  getAttribute,
  get,
  getOption,
  getValueType,
  canSet,
  set
};